import { useEffect, useState } from "react";
import { api, formatApiError, money } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { EST_STAGES, STAGE_ORDER } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Receipt, Plus, Warning, CheckCircle, PauseCircle, XCircle, PlayCircle, ClockCounterClockwise, Gear, FileText, Trash, CalendarBlank, FilePdf } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const FILTERS = [
  { key: "todas", label: "Todas" },
  { key: "en_aprobacion", label: "En aprobación" },
  { key: "por_pagar", label: "Por pagar" },
  { key: "pagado", label: "Pagadas" },
  { key: "suspendido", label: "Suspendidas" },
  { key: "cancelado", label: "Canceladas" },
];

function Stage({ status }) {
  const s = EST_STAGES[status] || { label: status, tone: "bg-slate-100 text-slate-600" };
  return <span className={`inline-flex px-2.5 py-1 rounded-sm text-xs font-semibold ${s.tone}`}>{s.label}</span>;
}

function PeriodPicker({ value, onChange }) {
  const [range, setRange] = useState();
  const [open, setOpen] = useState(false);
  const fmt = (d) => format(d, "d MMM yyyy", { locale: es });
  const handle = (r) => {
    setRange(r);
    if (r?.from && r?.to) onChange(`${fmt(r.from)} – ${fmt(r.to)}`);
    else if (r?.from) onChange(fmt(r.from));
    else onChange("");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="estimation-period" className={`${inputClass} flex items-center justify-between text-left ${value ? "" : "text-muted-foreground"}`}>
          <span className="truncate">{value || "Elegir fechas…"}</span>
          <CalendarBlank size={18} weight="bold" className="ml-2 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[120]" align="start">
        <Calendar mode="range" numberOfMonths={1} selected={range} onSelect={handle} locale={es} defaultMonth={range?.from} initialFocus />
        <div className="flex justify-between items-center gap-2 p-2 border-t">
          <button type="button" data-testid="estimation-period-clear" className="text-xs text-muted-foreground hover:text-foreground px-2 py-1" onClick={() => handle(undefined)}>Limpiar</button>
          <button type="button" data-testid="estimation-period-done" className="text-xs font-semibold text-primary px-2 py-1" onClick={() => setOpen(false)}>Listo</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Estimations() {
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const canDeleteEst = !!user; // temporal: todos pueden eliminar estimaciones hasta que se restrinjan privilegios
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [filter, setFilter] = useState("todas");
  const [fContractor, setFContractor] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ contract_id: "", number: "", period: "", amount_requested: "", progress_percent: "", description: "", amortizacion: "", fondo_garantia_pct: "", sanciones: "", deductivas: "", aditivas: "", devoluciones: "" });
  const [payFor, setPayFor] = useState(null);
  const [payNote, setPayNote] = useState("");
  const [payFile, setPayFile] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const [thOpen, setThOpen] = useState(false);
  const [thVal, setThVal] = useState("");
  const [facturaFor, setFacturaFor] = useState(null);
  const [facturaFile, setFacturaFile] = useState(null);
  const [facturaKind, setFacturaKind] = useState("factura");
  const [facturaBusy, setFacturaBusy] = useState(false);
  const [newFormato, setNewFormato] = useState(null);
  const [extracting, setExtracting] = useState(false);

  const load = () => api.get("/estimations").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/estimations/contract-options").then((r) => setContracts(r.data));
    api.get("/settings").then((r) => { setThreshold(r.data.approval_threshold); setThVal(r.data.approval_threshold); });
  }, []);

  const cmap = Object.fromEntries(contracts.map((c) => [c.id, c]));
  const ctorMap = Object.fromEntries(contractors.map((c) => [c.id, c.name]));
  const contractorOf = (e) => ctorMap[cmap[e.contract_id]?.contractor_id] || cmap[e.contract_id]?.contractor_name || "Sin contratista";

  const save = async (e) => {
    e.preventDefault();
    if (!form.contract_id) return toast.error("Selecciona un contrato");
    setBusy(true);
    try {
      const { contract_id, number, period, amount_requested, description } = form;
      const { data } = await api.post("/estimations", {
        contract_id, period, description, number: parseInt(number), amount_requested: parseFloat(amount_requested),
        amortizacion: parseFloat(form.amortizacion || 0),
        fondo_garantia_pct: parseFloat(form.fondo_garantia_pct || 0),
        sanciones: parseFloat(form.sanciones || 0),
        deductivas: parseFloat(form.deductivas || 0),
        aditivas: parseFloat(form.aditivas || 0),
        devoluciones: parseFloat(form.devoluciones || 0),
      });
      if (data.review?.over_estimation) toast.warning("⚠ Posible sobre-estimación detectada");
      else toast.success("Estimación registrada — inicia flujo de aprobación");
      if (newFormato) {
        const ffd = new FormData(); ffd.append("file", newFormato);
        try { await api.post(`/estimations/${data.id}/formato`, ffd); toast.success("Formato de estimación adjuntado"); } catch (e) {}
      }
      setOpen(false); setForm({ contract_id: "", number: "", period: "", amount_requested: "", progress_percent: "", description: "", amortizacion: "", fondo_garantia_pct: "", sanciones: "", deductivas: "", aditivas: "", devoluciones: "" });
      setNewFormato(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const advance = async (id, action, note = "", file = null) => {
    const fd = new FormData(); fd.append("action", action); fd.append("note", note);
    if (file) fd.append("file", file);
    try {
      const { data } = await api.post(`/estimations/${id}/advance`, fd);
      toast.success(`Estimación → ${EST_STAGES[data.status]?.label || data.status}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const money2 = (n) => (Number(n) || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const printCaratula = (e) => {
    const c = e.caratula || {};
    const num = (v, d = 0) => (v != null ? v : d);
    const importe = num(c.importe, e.amount_requested || 0);
    const amort = num(c.amortizacion, e.amortization || 0);
    const sanc = c.sanciones || 0, ded = c.deductivas || 0, adi = c.aditivas || 0, dev = c.devoluciones || 0;
    const fpct = num(c.fondo_garantia_pct, e.retencion_percent || 0);
    const fondo = num(c.fondo_garantia, e.retencion || 0);
    const sub = num(c.subtotal, importe - amort - sanc - ded + adi - dev);
    const iva = num(c.iva, sub * 0.16);
    const tot = num(c.total_neto, sub + iva - fondo);
    const contractTitle = (cmap[e.contract_id] || {}).title || "—";
    const contractor = ctorMap[e.contractor_id] || e.contractor_name || "—";
    const r = (label, val, neg) => `<tr><td>${label}</td><td class="n">${neg && val ? "\u2212" : ""}${money2(val)}</td></tr>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Carátula estimación #${e.number}</title>
<style>
*{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
body{margin:0;padding:32px;color:#111}
.hdr{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #FF5A00;padding-bottom:14px;margin-bottom:20px}
.brand{font-size:26px;font-weight:800;letter-spacing:1px;color:#111}
.brand small{display:block;font-size:11px;font-weight:600;color:#FF5A00;letter-spacing:2px;text-transform:uppercase}
.meta{text-align:right;font-size:12px;color:#444}
h1{font-size:16px;margin:0 0 12px;text-transform:uppercase;letter-spacing:1px}
.info{font-size:13px;margin-bottom:16px;line-height:1.7}
.info b{display:inline-block;min-width:150px;color:#555;font-weight:600}
table{width:100%;border-collapse:collapse;font-size:13px}
td{padding:8px 10px;border-bottom:1px solid #eee}
td.n{text-align:right;font-variant-numeric:tabular-nums}
tr.sub td{border-top:2px solid #111;font-weight:700}
tr.tot td{background:#FF5A00;color:#fff;font-weight:800;font-size:15px;border:0}
.foot{margin-top:34px;display:flex;justify-content:space-between;font-size:12px;color:#444}
.sign{border-top:1px solid #999;width:220px;text-align:center;padding-top:6px}
</style></head><body>
<div class="hdr"><div class="brand">PLAZA TARANTELLA<small>Gestión de Contratistas de Obra</small></div>
<div class="meta">Carátula de estimación<br><b>#${e.number}</b><br>${e.period || ""}</div></div>
<div class="info"><div><b>Contratista / Proveedor:</b> ${contractor}</div><div><b>Contrato / Obra:</b> ${contractTitle}</div><div><b>% Avance acumulado:</b> ${Math.round(e.progress_percent || 0)}%</div></div>
<h1>Desglose financiero</h1>
<table>
${r("Importe estimado", importe)}
${r("(−) Amortización de anticipo", amort, true)}
${r("(−) Sanciones", sanc, true)}
${r("(−) Deductivas", ded, true)}
${r("(+) Aditivas", adi)}
${r("(−) Devoluciones", dev, true)}
<tr class="sub">${`<td>Subtotal</td><td class="n">${money2(sub)}</td>`}</tr>
${r("(+) IVA (16%)", iva)}
${r(`(−) Fondo de garantía (${fpct}%)`, fondo, true)}
<tr class="tot">${`<td>TOTAL NETO A PAGAR</td><td class="n">${money2(tot)}</td>`}</tr>
</table>
<div class="foot"><div class="sign">Elaboró</div><div class="sign">Revisó</div><div class="sign">Autorizó (Dirección)</div></div>
<script>window.onload=function(){window.print();}<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
    else toast.error("Permite las ventanas emergentes para ver la carátula");
  };

  const doPay = async (e) => {
    e.preventDefault();
    await advance(payFor.id, "approve", payNote, payFile);
    setPayFor(null); setPayNote(""); setPayFile(null);
  };

  const removeEst = async (id) => {
    try { await api.delete(`/estimations/${id}`); toast.success("Estimación eliminada (queda registrada en Eliminados)"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadFactura = async (e) => {
    e.preventDefault();
    if (!facturaFile) return toast.error("Selecciona el archivo");
    setFacturaBusy(true);
    const fd = new FormData(); fd.append("file", facturaFile);
    try {
      await api.post(`/estimations/${facturaFor.id}/${facturaKind}`, fd);
      toast.success(facturaKind === "formato" ? "Formato adjuntado" : "Factura adjuntada"); setFacturaFor(null); setFacturaFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFacturaBusy(false); }
  };

  const deleteFactura = async (id) => {
    if (!window.confirm("¿Borrar la factura adjunta de esta estimación?")) return;
    try { await api.delete(`/estimations/${id}/factura`); toast.success("Factura borrada"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveThreshold = async () => {
    try {
      const { data } = await api.put("/settings/threshold", { value: parseFloat(thVal) });
      setThreshold(data.approval_threshold); setThOpen(false); toast.success("Umbral actualizado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const shown = items.filter((e) => {
    if (fContractor && contractorOf(e) !== fContractor) return false;
    if (filter === "todas") return true;
    if (filter === "en_aprobacion") return String(e.status).startsWith("pendiente_");
    return e.status === filter;
  });

  return (
    <div>
      <PageHeader overline="Aprobación y pagos" title="Estimaciones"
        action={
          <div className="flex items-center gap-2">
            {isManager && (
              <button onClick={() => window.open(`${api.defaults.baseURL}/export/estimations.csv`, "_blank")} data-testid="export-est-csv"
                className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
                <FileText size={18} weight="bold" /> Exportar CSV
              </button>
            )}
            <PrimaryButton onClick={() => setOpen(true)} data-testid="add-estimation-btn"><Plus size={18} weight="bold" /> Nueva</PrimaryButton>
          </div>
        } />

      {/* threshold banner */}
      <Card className="p-4 mb-5 flex flex-wrap items-center justify-between gap-3 border-l-4 border-l-primary">
        <p className="text-sm text-muted-foreground">
          Flujo: <b className="text-foreground">G. Área → Superintendente → G. Administrativo</b>. Montos mayores a{" "}
          <b className="text-foreground">{threshold != null ? money2(threshold) : "…"}</b> requieren autorización del <b className="text-foreground">Gerente General</b> antes de pasar a pagos.
        </p>
        {user?.role === "admin" && (
          <button onClick={() => setThOpen(true)} data-testid="edit-threshold-btn" className="inline-flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline">
            <Gear size={16} weight="bold" /> Cambiar umbral
          </button>
        )}
      </Card>

      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`est-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-200 ${filter === f.key ? "bg-primary text-white border-primary" : "bg-white border-input text-muted-foreground hover:bg-muted"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {contracts.length > 0 && (
        <div className="flex items-center gap-2 mb-5" data-testid="est-contractor-filter-row">
          <span className="text-xs font-semibold text-muted-foreground">Contratista / proveedor:</span>
          <select data-testid="est-contractor-filter" value={fContractor} onChange={(e) => setFContractor(e.target.value)} className="h-9 px-3 rounded-sm border border-input bg-white text-sm">
            <option value="">Todos</option>
            {[...new Set(contracts.map((c) => ctorMap[c.contractor_id] || c.contractor_name || "Sin contratista"))].sort().map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {fContractor && <span className="text-xs text-muted-foreground">{shown.length} estimación(es)</span>}
        </div>
      )}

      {fContractor && shown.length > 0 && (
        <Card className="p-4 mb-5 flex flex-wrap gap-6 border-l-4 border-l-primary" data-testid="est-contractor-subtotal">
          <div><div className="text-xs text-muted-foreground">Bruto · {fContractor}</div><div className="font-heading font-bold text-lg">{money2(shown.reduce((s, e) => s + (e.amount_requested || 0), 0))}</div></div>
          <div><div className="text-xs text-muted-foreground">Amortizado</div><div className="font-heading font-bold text-lg">-{money2(shown.reduce((s, e) => s + (e.amortization || 0), 0))}</div></div>
          <div><div className="text-xs text-muted-foreground">Neto</div><div className="font-heading font-bold text-lg text-primary">{money2(shown.reduce((s, e) => s + ((e.net_amount != null ? e.net_amount : e.amount_requested) || 0), 0))}</div></div>
          <div><div className="text-xs text-muted-foreground">Estimaciones</div><div className="font-heading font-bold text-lg">{shown.length}</div></div>
        </Card>
      )}

      {shown.length === 0 ? (
        <EmptyState icon={Receipt} title="Sin estimaciones" subtitle="Registra cobros por avance; pasan por el flujo de autorización antes de pagarse."
          action={<PrimaryButton onClick={() => setOpen(true)}><Plus size={18} weight="bold" /> Nueva estimación</PrimaryButton>} />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["#", "Contrato", "Contratista", "Bruto", "Amort.", "Neto", "IA", "Documentos", "Etapa", "Acciones"].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr key={e.id} data-testid={`estimation-row-${e.id}`} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors duration-150">
                  <td className="px-4 py-3 font-semibold">{e.number}</td>
                  <td className="px-4 py-3">{cmap[e.contract_id]?.title || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{contractorOf(e)}</td>
                  <td className="px-4 py-3">{money2(e.amount_requested)}</td>
                  <td className="px-4 py-3 text-muted-foreground">-{money2(e.amortization || 0)}</td>
                  <td className="px-4 py-3 font-semibold text-primary">{money2(e.net_amount != null ? e.net_amount : e.amount_requested)}</td>
                  <td className="px-4 py-3">
                    {e.review?.over_estimation
                      ? <span title="Sobre-estimación" className="text-danger"><Warning size={18} weight="fill" /></span>
                      : <span title="Correcta" className="text-success"><CheckCircle size={18} weight="fill" /></span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground w-16">Formato</span>
                        {e.formato_path ? (
                          <a href={`${api.defaults.baseURL}/files/${e.formato_path}`} target="_blank" rel="noreferrer" download={e.formato_name || true} data-testid={`formato-view-${e.id}`} className="inline-flex items-center gap-1 font-semibold text-success hover:underline"><FileText size={13} weight="fill" /> Ver</a>
                        ) : isManager && e.status !== "cancelado" ? (
                          <button onClick={() => { setFacturaKind("formato"); setFacturaFor(e); setFacturaFile(null); }} data-testid={`formato-upload-${e.id}`} className="inline-flex items-center gap-1 font-semibold text-amber-600 hover:underline"><Plus size={12} weight="bold" /> Subir</button>
                        ) : <span className="text-muted-foreground">—</span>}
                      </div>
                      {(e.status === "por_pagar" || e.status === "pagado") && (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16">Factura</span>
                            {e.factura_path ? (
                              <>
                                <a href={`${api.defaults.baseURL}/files/${e.factura_path}`} target="_blank" rel="noreferrer" download={e.factura_name || true} data-testid={`factura-view-${e.id}`} className="inline-flex items-center gap-1 font-semibold text-success hover:underline"><FileText size={13} weight="fill" /> Ver</a>
                                {isManager && <button onClick={() => { setFacturaKind("factura"); setFacturaFor(e); setFacturaFile(null); }} data-testid={`factura-replace-${e.id}`} className="font-semibold text-amber-600 hover:underline">Reemplazar</button>}
                              </>
                            ) : isManager ? (
                              <button onClick={() => { setFacturaKind("factura"); setFacturaFor(e); setFacturaFile(null); }} data-testid={`factura-upload-${e.id}`} className="inline-flex items-center gap-1 font-semibold text-amber-600 hover:underline"><Plus size={12} weight="bold" /> Subir</button>
                            ) : <span className="text-danger">Falta</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16">Comprob.</span>
                            {e.paid_receipt_path ? (
                              <a href={`${api.defaults.baseURL}/files/${e.paid_receipt_path}`} target="_blank" rel="noreferrer" data-testid={`comprobante-view-${e.id}`} className="inline-flex items-center gap-1 font-semibold text-success hover:underline"><FileText size={13} weight="fill" /> Ver</a>
                            ) : <span className="text-danger">Falta</span>}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3"><Stage status={e.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      {isManager && String(e.status).startsWith("pendiente_") && (
                        <button onClick={() => e.factura_path ? advance(e.id, "approve") : toast.error("Debes subir la factura antes de autorizar")} data-testid={`approve-${e.id}`} title={e.factura_path ? "Autorizar" : "Falta la factura"} className={`p-1.5 rounded-sm ${e.factura_path ? "text-success hover:bg-emerald-50" : "text-slate-300 hover:bg-slate-50"}`}><CheckCircle size={18} weight="fill" /></button>
                      )}
                      {isManager && e.status === "por_pagar" && (
                        <button onClick={() => setPayFor(e)} data-testid={`pay-${e.id}`} className="px-2 py-1 text-xs font-semibold text-white bg-primary rounded-sm hover:bg-orange-600">Aplicar pago</button>
                      )}
                      {isManager && (String(e.status).startsWith("pendiente_") || e.status === "por_pagar") && (
                        <>
                          <button onClick={() => advance(e.id, "suspend")} data-testid={`suspend-${e.id}`} title="Suspender" className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-sm"><PauseCircle size={18} weight="fill" /></button>
                          <button onClick={() => window.confirm("¿Cancelar estimación?") && advance(e.id, "cancel")} title="Cancelar" className="p-1.5 text-danger hover:bg-red-50 rounded-sm"><XCircle size={18} weight="fill" /></button>
                        </>
                      )}
                      {isManager && e.status === "suspendido" && (
                        <button onClick={() => advance(e.id, "resume")} data-testid={`resume-${e.id}`} title="Reanudar" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-sm"><PlayCircle size={18} weight="fill" /></button>
                      )}
                      {e.status === "pagado" && e.paid_receipt_path && (
                        <a href={`${api.defaults.baseURL}/files/${e.paid_receipt_path}`} target="_blank" rel="noreferrer" title="Comprobante" className="p-1.5 text-primary hover:bg-orange-50 rounded-sm"><FileText size={18} weight="fill" /></a>
                      )}
                      <button onClick={() => printCaratula(e)} data-testid={`caratula-${e.id}`} title="Carátula (PDF)" className="p-1.5 text-primary hover:bg-orange-50 rounded-sm"><FilePdf size={18} weight="fill" /></button>
                      <button onClick={() => setHistoryFor(e)} title="Historial" className="p-1.5 text-muted-foreground hover:bg-muted rounded-sm"><ClockCounterClockwise size={18} /></button>
                      {canDeleteEst && <button onClick={() => window.confirm("¿Eliminar estimación? Quedará registrada en Eliminados.") && removeEst(e.id)} data-testid={`delete-est-${e.id}`} title="Eliminar" className="p-1.5 text-danger hover:bg-red-50 rounded-sm"><Trash size={18} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* New estimation */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nueva estimación">
        <form onSubmit={save} className="space-y-4">
          <Field label="Contrato *">
            <select data-testid="estimation-contract" required className={inputClass} value={form.contract_id} onChange={(e) => { const cid = e.target.value; const nums = items.filter((x) => x.contract_id === cid).map((x) => x.number || 0); const next = (nums.length ? Math.max(...nums) : 0) + 1; const cc = cmap[cid] || {}; setForm({ ...form, contract_id: cid, number: cid ? String(next) : "", fondo_garantia_pct: cid && cc.retencion_percent ? String(cc.retencion_percent) : form.fondo_garantia_pct }); }}>
              <option value="">Selecciona…</option>
              {Object.entries(contracts.reduce((acc, c) => { const k = ctorMap[c.contractor_id] || c.contractor_name || "Sin contratista"; (acc[k] = acc[k] || []).push(c); return acc; }, {})).sort((a, b) => a[0].localeCompare(b[0])).map(([grp, list]) => (
                <optgroup key={grp} label={grp}>
                  {list.map((c) => <option key={c.id} value={c.id}>{c.title} — {money2(c.amount)}{c.anticipo_percent > 0 ? ` (ant. ${c.anticipo_percent}%)` : ""}</option>)}
                </optgroup>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Número *"><input data-testid="estimation-number" type="number" required className={inputClass} value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} /></Field>
            <Field label="Periodo"><PeriodPicker key={open ? "po" : "pc"} value={form.period} onChange={(v) => setForm({ ...form, period: v })} /></Field>
          </div>
          <Field label="Importe estimado (bruto) *"><input data-testid="estimation-amount" type="number" step="0.01" required className={inputClass} value={form.amount_requested} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} placeholder={extracting ? "Leyendo formato…" : "Captúralo o súbelo del formato"} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amortización de anticipo"><input data-testid="est-amortizacion" type="number" step="0.01" className={inputClass} value={form.amortizacion} onChange={(e) => setForm({ ...form, amortizacion: e.target.value })} placeholder="0.00" /></Field>
            <Field label="Fondo de garantía (%)"><input data-testid="est-fondo-pct" type="number" step="0.01" className={inputClass} value={form.fondo_garantia_pct} onChange={(e) => setForm({ ...form, fondo_garantia_pct: e.target.value })} placeholder="0" /></Field>
            <Field label="Sanciones"><input data-testid="est-sanciones" type="number" step="0.01" className={inputClass} value={form.sanciones} onChange={(e) => setForm({ ...form, sanciones: e.target.value })} placeholder="0.00" /></Field>
            <Field label="Deductivas"><input data-testid="est-deductivas" type="number" step="0.01" className={inputClass} value={form.deductivas} onChange={(e) => setForm({ ...form, deductivas: e.target.value })} placeholder="0.00" /></Field>
            <Field label="Aditivas"><input data-testid="est-aditivas" type="number" step="0.01" className={inputClass} value={form.aditivas} onChange={(e) => setForm({ ...form, aditivas: e.target.value })} placeholder="0.00" /></Field>
            <Field label="Devoluciones"><input data-testid="est-devoluciones" type="number" step="0.01" className={inputClass} value={form.devoluciones} onChange={(e) => setForm({ ...form, devoluciones: e.target.value })} placeholder="0.00" /></Field>
          </div>
          {(() => {
            const imp = parseFloat(form.amount_requested || 0);
            const am = parseFloat(form.amortizacion || 0), sa = parseFloat(form.sanciones || 0), de = parseFloat(form.deductivas || 0), ad = parseFloat(form.aditivas || 0), dv = parseFloat(form.devoluciones || 0);
            const fpct = parseFloat(form.fondo_garantia_pct || 0);
            const fondo = imp * fpct / 100; const sub = imp - am - sa - de + ad - dv; const iva = sub * 0.16; const tot = sub + iva - fondo;
            return (
              <div className="rounded-sm bg-slate-900 text-white p-3 text-sm space-y-1" data-testid="caratula-totals">
                <div className="flex justify-between"><span className="text-slate-300">Subtotal</span><b>{money2(sub)}</b></div>
                <div className="flex justify-between"><span className="text-slate-300">IVA (16%)</span><b>{money2(iva)}</b></div>
                <div className="flex justify-between"><span className="text-slate-300">Fondo de garantía ({fpct || 0}%)</span><b>-{money2(fondo)}</b></div>
                <div className="flex justify-between pt-1 border-t border-slate-700 text-primary text-base"><span>Total neto</span><b data-testid="caratula-total-neto">{money2(tot)}</b></div>
              </div>
            );
          })()}
          {(() => {
            const c = cmap[form.contract_id];
            const amt = parseFloat(form.amount_requested || 0);
            if (!c || !amt) return null;
            const prevGross = items.filter((e) => e.contract_id === form.contract_id && !["cancelado", "suspendido"].includes(e.status)).reduce((s, e) => s + (e.amount_requested || 0), 0);
            const cumulative = prevGross + amt;
            const total = c.amount || 0;
            const over = total > 0 && cumulative > total;
            return (
              <div className={`text-xs rounded-sm p-2.5 ${over ? "bg-red-50 text-danger" : "bg-muted text-muted-foreground"}`} data-testid="overrun-warning">
                Acumulado: <b>{money2(cumulative)}</b> de <b>{money2(total)}</b> ({total > 0 ? Math.round((cumulative / total) * 100) : 0}%). Saldo: <b>{money2(total - cumulative)}</b>
                {over && <div className="font-bold mt-1">⚠ Este monto hace que el acumulado supere el total del contrato.</div>}
              </div>
            );
          })()}
          <p className="text-xs text-muted-foreground">El <b>% de avance acumulado</b> se calcula automáticamente con los montos de las estimaciones (no es editable). Captura la amortización, sanciones, deductivas, aditivas y devoluciones; el Subtotal, IVA y Total neto se calculan solos.</p>
          <Field label="Formato de estimación (obligatorio: de aquí se lee el monto)">
            <input data-testid="estimation-formato" type="file" accept="application/pdf,image/*" onChange={async (e) => {
              const file = e.target.files[0]; setNewFormato(file);
              if (!file) return;
              setExtracting(true);
              try {
                const fd = new FormData(); fd.append("file", file);
                const { data } = await api.post("/estimations/extract-formato", fd);
                if (data.amount != null) { setForm((prev) => ({ ...prev, amount_requested: data.amount })); toast.success(`Monto leído del formato: ${money2(data.amount)}`); }
                else toast.error("No se pudo leer el monto; sube un formato más legible (PDF/imagen clara)");
              } catch { toast.error("No se pudo leer el formato"); }
              finally { setExtracting(false); }
            }}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
            <p className="text-xs text-muted-foreground mt-1">{extracting ? "Leyendo el monto del formato…" : "Sube el formato de estimación; el Monto bruto se lee automáticamente. La factura y el comprobante se suben después de aprobarse."}</p>
          </Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-estimation-btn">{busy ? "Guardando…" : "Registrar"}</PrimaryButton>
        </form>
      </Modal>

      {/* Pay modal */}
      <Modal open={!!payFor} onClose={() => setPayFor(null)} title={`Aplicar pago · Estimación #${payFor?.number || ""}`}>
        {payFor && (
          <form onSubmit={doPay} className="space-y-4">
            <div className="bg-slate-900 text-white rounded-sm p-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-300">Bruto</span><b>{money2(payFor.amount_requested)}</b></div>
              <div className="flex justify-between"><span className="text-slate-300">Amortización anticipo</span><b>-{money2(payFor.amortization || 0)}</b></div>
              <div className="flex justify-between mt-1 pt-1 border-t border-slate-700"><span className="text-slate-300">Neto a pagar</span><b className="text-primary">{money2(payFor.net_amount != null ? payFor.net_amount : payFor.amount_requested)}</b></div>
            </div>
            <Field label="Comprobante de pago (subir)">
              <input data-testid="pay-file" type="file" accept="application/pdf,image/*" onChange={(e) => setPayFile(e.target.files[0])}
                className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
            </Field>
            <p className="text-xs text-muted-foreground -mt-2">Para marcar como <b>pagada</b> debe estar subida la <b>factura</b> y el <b>comprobante</b>.</p>
            <Field label="Referencia / nota del pago"><input data-testid="pay-note" className={inputClass} placeholder="Ej. Transferencia SPEI folio 12345" value={payNote} onChange={(e) => setPayNote(e.target.value)} /></Field>
            <PrimaryButton type="submit" data-testid="confirm-pay-btn">Confirmar pago</PrimaryButton>
          </form>
        )}
      </Modal>

      {/* Documento (formato / factura) */}
      <Modal open={!!facturaFor} onClose={() => setFacturaFor(null)} title={`${facturaKind === "formato" ? "Formato de estimación" : "Factura"} · Estimación #${facturaFor?.number || ""}`}>
        <form onSubmit={uploadFactura} className="space-y-4">
          <Field label={facturaKind === "formato" ? "Archivo del formato de estimación (PDF/imagen)" : "Archivo de la factura (PDF/imagen)"}>
            <input data-testid="doc-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFacturaFile(e.target.files[0])}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <PrimaryButton type="submit" disabled={facturaBusy} data-testid="confirm-doc-btn">{facturaBusy ? "Subiendo…" : "Adjuntar"}</PrimaryButton>
        </form>
      </Modal>

      {/* History modal */}
      <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} title={`Historial · Estimación #${historyFor?.number || ""}`}>
        {historyFor && (
          <div className="space-y-3">
            {(historyFor.history || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin movimientos aún.</p>
            ) : historyFor.history.map((h, i) => (
              <div key={i} className="border-l-2 border-primary pl-3">
                <div className="text-sm font-semibold capitalize">{h.action} → {EST_STAGES[h.to]?.label || h.to}</div>
                <div className="text-xs text-muted-foreground">{h.by} ({h.role}) · {new Date(h.at).toLocaleString("es-MX")}</div>
                {h.note && <div className="text-xs mt-0.5">"{h.note}"</div>}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Threshold modal */}
      <Modal open={thOpen} onClose={() => setThOpen(false)} title="Umbral de autorización (Gerente General)">
        <div className="space-y-4">
          <Field label="Monto a partir del cual se requiere autorización del Gerente General (MXN)">
            <input data-testid="threshold-input" type="number" step="1000" className={inputClass} value={thVal} onChange={(e) => setThVal(e.target.value)} />
          </Field>
          <PrimaryButton onClick={saveThreshold} data-testid="save-threshold-btn">Guardar umbral</PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}

